import { useState } from 'react';
import { useLoaderData, useLocation, useNavigate } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Button, Card, Icon, KpiCard } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, EmptyState } from '@ds/feedback';
import { Tabs } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { dataSimples } from '@/comuns/formato';
import { dados } from '@/data/porta';
import { pode } from '@/session/sessao';
import type { CargaDaClinicaNaPlataforma, EventoDeAuditoria, UnidadeDaClinica, UsuarioNaPlataforma } from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { SuspensaoModal } from './AcoesDaClinica';
import { AbaAssinatura, AbaFaturasDaClinica, AbaIaDaClinica } from './CobrancaDaClinica';
import { DetalheDaAuditoriaModal } from './DetalheDaAuditoriaModal';
import { ImpersonarModal } from './ImpersonarModal';
import { SITUACAO_DA_CLINICA, quandoRelativo } from './Visao';

export async function carregarClinicaDaPlataforma({ params }: LoaderFunctionArgs): Promise<CargaDaClinicaNaPlataforma> {
    const id = params.clinicaId;
    if (!id) throw new Response('Clínica não informada', { status: 404 });
    return dados().clinicaDaPlataforma(id);
}

export function Clinica() {
    const carga = useLoaderData() as CargaDaClinicaNaPlataforma;
    const navigate = useNavigate();
    const [entrando, setEntrando] = useState(false);
    const [suspensao, setSuspensao] = useState(false);
    const [evento, setEvento] = useState<EventoDeAuditoria | null>(null);
    const rota = rotaPorId('clinica');
    const c = carga.clinica;

    /* O caminho vem do roteador, e não de `window.location`: são a mesma coisa
       no navegador e coisas diferentes em teste e em qualquer render fora dele.
       A subrota é o fim do caminho, mas o id da clínica também é um segmento —
       sem a lista de abas conhecidas, `/admin/clinicas/<id>` leria o próprio id
       como aba. */
    const ultimo = useLocation().pathname.split('/').pop() ?? '';
    const aba = (rota?.children ?? []).some((x) => x.path === ultimo) ? ultimo : 'resumo';

    const colunasDeUsuario: DataTableColumn<UsuarioNaPlataforma>[] = [
        {
            key: 'nome', header: 'Pessoa',
            render: (u) => (
                <span>
                    <strong style={{ display: 'block', fontWeight: 600 }}>{u.nome}</strong>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{u.email}</span>
                </span>
            ),
        },
        { key: 'papel', header: 'Papel', width: 150 },
        {
            key: 'acesso', header: 'Acesso', width: 160,
            render: (u) => <Badge tone={u.acesso === 'ativo' ? 'success' : u.acesso === 'convite-pendente' ? 'warning' : 'neutral'}>{u.acesso}</Badge>,
        },
        {
            key: 'ultimoAcesso', header: 'Último acesso', align: 'right', width: 140,
            render: (u) => quandoRelativo(u.ultimoAcesso),
        },
    ];

    const colunasDeUnidade: DataTableColumn<UnidadeDaClinica>[] = [
        { key: 'nome', header: 'Unidade', render: (u) => <strong style={{ fontWeight: 600 }}>{u.nome}</strong> },
        { key: 'telefone', header: 'Telefone', width: 160, render: (u) => u.telefone || '—' },
        {
            key: 'cidade', header: 'Endereço',
            render: (u) => [u.rua && `${u.rua}, ${u.numero}`, u.bairro, u.cidade && `${u.cidade}/${u.estado}`].filter(Boolean).join(' · ') || '—',
        },
        { key: 'salas', header: 'Salas', align: 'right', width: 90, render: (u) => (u.salas === undefined ? '—' : String(u.salas)) },
    ];

    const colunasDeHistorico: DataTableColumn<EventoDeAuditoria>[] = [
        { key: 'ocorridoEm', header: 'Quando', width: 150, render: (e) => dataSimples(e.ocorridoEm.slice(0, 10)) },
        { key: 'acao', header: 'Ação' },
        { key: 'tabela', header: 'Tabela', width: 160 },
        { key: 'autorNome', header: 'Autor', width: 160 },
    ];

    const podeSuspender = pode('clientes.suspender') && c.situacao !== 'cancelada';

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title={c.nome}
                description={`${c.apelido} · plano ${c.plano} · cliente desde ${dataSimples(c.criadoEm.slice(0, 10))}`}
                actions={(
                    <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button variant="secondary" onClick={() => navigate('/admin/clinicas/todas')} iconLeft={<Icon name="arrow-left" size={15} />}>
                            Voltar à lista
                        </Button>
                        {podeSuspender && (
                            <Button
                                variant={c.situacao === 'suspensa' ? 'secondary' : 'ghost'}
                                iconLeft={<Icon name={c.situacao === 'suspensa' ? 'rotate-ccw' : 'ban'} size={15} />}
                                onClick={() => setSuspensao(true)}
                            >
                                {c.situacao === 'suspensa' ? 'Reativar' : 'Suspender acesso'}
                            </Button>
                        )}
                        {/* O único caminho da plataforma para dentro do tenant.
                            Leva ao pedido de motivo — entrar é escrita, e não
                            acontece por navegação. */}
                        <Button onClick={() => setEntrando(true)} iconLeft={<Icon name="eye" size={15} />}>
                            Entrar na clínica
                        </Button>
                    </span>
                )}
            >
                <Tabs
                    ariaLabel="Seções da ficha" activeId={aba}
                    onChange={(id) => navigate(`/admin/clinicas/${c.id}/${id}`)}
                    tabs={(rota?.children ?? []).map((x) => ({ id: x.path, label: x.label }))}
                />
            </PageHeader>

            {c.situacao === 'suspensa' && (
                <Alert tone="warning" dismissible={false} title="Acesso suspenso">
                    Ninguém da equipe desta clínica lê nem escreve nada enquanto a suspensão durar. Os dados estão
                    preservados; reativar devolve o acesso.
                </Alert>
            )}

            {aba === 'resumo' && (
                <>
                    <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                        <KpiCard label="Situação" value={SITUACAO_DA_CLINICA[c.situacao].rotulo} hint={`plano ${c.plano}`} icon={<Icon name="building-2" size={16} />} />
                        <KpiCard label="Pessoas com acesso" value={String(c.membrosAtivos)} hint="membros ativos" icon={<Icon name="users" size={16} />} />
                        <KpiCard label="Contatos" value={String(c.contatos)} hint="pacientes e leads" icon={<Icon name="contact" size={16} />} />
                        <KpiCard label="Agendamentos em 30 dias" value={String(c.agendamentos30d)} hint={`último acesso ${quandoRelativo(c.ultimoAcesso)}`} icon={<Icon name="calendar-days" size={16} />} />
                    </div>

                    {/* O poder de entrar deixa rastro, e o rastro fica à vista na
                        própria ficha de quem foi visitado. */}
                    <Card title="Impersonações" subtitle="Quem entrou nesta clínica, por quê, e se ainda está dentro" padding={0}>
                        {carga.impersonacoes.length === 0 ? (
                            <EmptyState compact icon="eye-off" title="Ninguém entrou nesta clínica" description="Nenhuma impersonação registrada." />
                        ) : (
                            <DataTable
                                columns={[
                                    { key: 'operador', header: 'Operador' },
                                    { key: 'motivo', header: 'Motivo' },
                                    { key: 'iniciadaEm', header: 'Início', width: 150, render: (i) => dataSimples(i.iniciadaEm.slice(0, 10)) },
                                    { key: 'viva', header: 'Estado', width: 120, render: (i) => <Badge tone={i.viva ? 'warning' : 'neutral'}>{i.viva ? 'Aberta' : 'Encerrada'}</Badge> },
                                ]}
                                rows={carga.impersonacoes} rowKey="id"
                                stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                            />
                        )}
                    </Card>
                </>
            )}

            {aba === 'assinatura' && <AbaAssinatura carga={carga} />}

            {aba === 'unidades' && (
                <Card padding={0}>
                    {/* A lista vem de uma função do painel: cadastro da unidade e
                        contagem de salas, sem abrir a tabela ao operador. */}
                    {carga.unidades.length === 0 ? (
                        <EmptyState icon="map-pin" title="Nenhuma unidade cadastrada" description="Clínica aberta pelo painel já nasce com a unidade principal." />
                    ) : (
                        <DataTable
                            columns={colunasDeUnidade} rows={carga.unidades} rowKey="id"
                            stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                        />
                    )}
                </Card>
            )}

            {aba === 'usuarios' && (
                <Card padding={0}>
                    <DataTable
                        columns={colunasDeUsuario} rows={carga.usuarios} rowKey="id"
                        stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                        emptyMessage="Nenhuma pessoa cadastrada nesta clínica."
                    />
                </Card>
            )}

            {aba === 'faturas' && <AbaFaturasDaClinica carga={carga} />}

            {aba === 'ia' && <AbaIaDaClinica carga={carga} />}

            {aba === 'historico' && (
                <Card padding={0}>
                    <DataTable
                        columns={colunasDeHistorico} rows={carga.auditoria} rowKey="id"
                        onRowClick={setEvento}
                        rowLabel={(e) => `${e.acao} · ${dataSimples(e.ocorridoEm.slice(0, 10))}`}
                        stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                        emptyMessage="Nenhum evento registrado para esta clínica."
                    />
                </Card>
            )}

            <ImpersonarModal
                clinicaId={c.id} clinica={c.nome}
                aberto={entrando} onFechar={() => setEntrando(false)}
            />
            <SuspensaoModal clinica={c} aberto={suspensao} onFechar={() => setSuspensao(false)} />
            <DetalheDaAuditoriaModal evento={evento} onFechar={() => setEvento(null)} />
        </div>
    );
}
